const clientId = process.env.REACT_APP_SPOTIFY_CLIENT_ID || '40a4d0c2c361443bb4f8e8e6f045e7b8';
const redirectUri = process.env.REACT_APP_SPOTIFY_REDIRECT_URI || `${window.location.origin}/callback`;
const authEndpoint = 'https://accounts.spotify.com/authorize';
const tokenEndpoint = 'https://accounts.spotify.com/api/token';

const AUTH_STORAGE_KEY = 'spotifyAuth';
const PKCE_VERIFIER_KEY = 'spotifyPkceVerifier';
const AUTH_STATE_KEY = 'spotifyAuthState';
const RETURN_PATH_KEY = 'spotifyReturnPath';
const AUTH_PROCESSING_KEY = 'spotifyAuthProcessing';

export const scopes = [
  'user-read-private',
  'user-read-email',
  'playlist-read-private',
  'playlist-modify-public',
  'user-top-read',
];

export const userProfileEndpoint = 'https://api.spotify.com/v1/me';
export const userPlaylistsEndpoint = 'https://api.spotify.com/v1/me/playlists';
export const trendingSongsEndpoint = 'https://api.spotify.com/v1/playlists/37i9dQZEVXbMDoHDwVN2tF/tracks';

const toBase64Url = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes)))
  .replace(/=/g, '')
  .replace(/\+/g, '-')
  .replace(/\//g, '_');

const randomString = (length = 64) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(values, (value) => alphabet[value % alphabet.length]).join('');
};

const createCodeChallenge = async (verifier) => {
  const encoded = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return toBase64Url(digest);
};

const isSafeReturnPath = (path) => typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

const saveAuth = (tokenResponse) => {
  const previous = getStoredAuth();
  const auth = {
    accessToken: tokenResponse.access_token,
    refreshToken: tokenResponse.refresh_token || previous?.refreshToken,
    expiresAt: Date.now() + tokenResponse.expires_in * 1000,
  };
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
  return auth;
};

export const getStoredAuth = () => {
  try {
    const value = localStorage.getItem(AUTH_STORAGE_KEY);
    return value ? JSON.parse(value) : null;
  } catch {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
};

export const clearSpotifyAuth = () => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(AUTH_STATE_KEY);
  sessionStorage.removeItem(RETURN_PATH_KEY);
  sessionStorage.removeItem(AUTH_PROCESSING_KEY);
};

const requestToken = async (parameters) => {
  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: clientId, ...parameters }),
  });

  if (!response.ok) {
    throw new Error('Spotify could not complete authentication. Please log in again.');
  }

  return response.json();
};

export const getSpotifyAuthUrl = async (returnPath = '/') => {
  const verifier = randomString();
  const state = randomString(32);
  const challenge = await createCodeChallenge(verifier);

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(AUTH_STATE_KEY, state);
  sessionStorage.setItem(RETURN_PATH_KEY, isSafeReturnPath(returnPath) ? returnPath : '/');

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: scopes.join(' '),
    state,
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });

  return `${authEndpoint}?${params.toString()}`;
};

export const getAuthCallbackParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
  };
};

export const completeSpotifyAuth = async (code, state) => {
  const expectedState = sessionStorage.getItem(AUTH_STATE_KEY);
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);

  if (!state || state !== expectedState || !verifier) {
    throw new Error('The Spotify login response could not be verified. Please try again.');
  }

  const tokenResponse = await requestToken({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    code_verifier: verifier,
  });
  const auth = saveAuth(tokenResponse);
  const returnPath = sessionStorage.getItem(RETURN_PATH_KEY) || '/';
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(AUTH_STATE_KEY);
  sessionStorage.removeItem(RETURN_PATH_KEY);
  return { token: auth.accessToken, returnPath };
};

export const restoreSpotifySession = async () => {
  const auth = getStoredAuth();
  if (!auth?.accessToken) return null;
  if (auth.expiresAt > Date.now() + 60_000) return auth.accessToken;
  if (!auth.refreshToken) {
    clearSpotifyAuth();
    return null;
  }

  try {
    const tokenResponse = await requestToken({ grant_type: 'refresh_token', refresh_token: auth.refreshToken });
    return saveAuth(tokenResponse).accessToken;
  } catch {
    clearSpotifyAuth();
    return null;
  }
};

export const getUserTopArtists = async (token) => {
  const response = await fetch('https://api.spotify.com/v1/me/top/artists', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch top artists');
  return (await response.json()).items;
};
